<template>
    <tailwind-layout title="Agent RentRisk Verification">
        <div class="bg-rent-risk-blue-100 overflow-hidden sm:rounded-lg">
            <div class="relative max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <h4 class="text-3xl text-center font-bold leading-7 text-gray-700 sm:text-2xl sm:truncate">
                    Personal Information
                </h4>
                <h5 class="text-lg text-center font-bold leading-7 text-gray-500 sm:text-lg sm:truncate">
                    My Personal Information (<span class="underline italic">do not</span> enter your agency's information here).
                </h5>
                <h5 class="text-lg font-bold text-gray-700 sm:text-lg sm:truncate text-center">
                    You must enter your personal information to be verified by Experian as a user.
                </h5>
                <form @submit.prevent="update" class="mt-20 space-y-8 divide-y divide-gray-200">
                    <input type="hidden" id="user_prefs" name="user_prefs"> <input type="hidden" id="user_prefs2" name="user_prefs2">
                    <div class="space-y-8 divide-y divide-gray-200">
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div class="sm:col-span-2">
                                <label for="first_name" v-bind:class="(form.errors.first_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    First Name <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="first_name" v-model="form.first_name" :error="form.errors.first_name" type="text" v-bind:class="(form.errors.first_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.first_name" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="middle_name" v-bind:class="(form.errors.middle_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Middle Name
                                </label>
                                <div class="mt-1">
                                    <input id="middle_name" :disabled="disabled" v-model="form.middle_name" :error="form.errors.middle_name" type="text" :class="{'bg-gray-50 cursor-not-allowed': this.disabled}" v-bind:class="(form.errors.middle_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.middle_name" class="text-red-500 text-sm"></p>
                                    <input type="checkbox" class="focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300 rounded" @click="isDisabled()" /> <span class="text-sm">I do not have a middle name</span>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="last_name" v-bind:class="(form.errors.last_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Last Name <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="last_name" v-model="form.last_name" :error="form.errors.last_name" type="text" v-bind:class="(form.errors.last_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.last_name" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-6">
                                <div class="rounded-md bg-yellow-50 p-4">
                                    <div class="flex">
                                        <div class="flex-shrink-0">
                                            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path>
                                            </svg>
                                        </div>
                                        <div class="ml-3">
                                            <h3 class="text-sm font-medium text-yellow-800">Living Overseas?</h3>
                                            <div class="mt-2 text-sm text-yellow-700">
                                                <p>If you are currently living overseas, please use your last U.S. based address. Foreign addresses or military (APO or FPO) or DoD addresses overseas will NOT work.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="sm:col-span-3">
                                <label for="address-1" v-bind:class="(form.errors.address_1)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 1 <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="address-1" v-model="form.address_1" :error="form.errors.address_1" type="text" v-bind:class="(form.errors.address_1)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.address_1" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <label for="address-2" v-bind:class="(form.errors.address_2)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 2
                                </label>
                                <div class="mt-1">
                                    <input id="address-2" v-model="form.address_2" :error="form.errors.address_2" type="text" v-bind:class="(form.errors.address_2)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="city" v-bind:class="(form.errors.city)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    City <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="city" v-model="form.city" :error="form.errors.city" type="text" v-bind:class="(form.errors.city)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.city" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="state" v-bind:class="(form.errors.state)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    State <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
<!--                                    <input id="state" v-model="form.state" :error="form.errors.state" type="text" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">-->
                                    <select-input :id="state" v-bind:value="form.state" v-model="form.state" :error="form.errors.state" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm'">
                                        <option v-for="(state, index) in states" :value="index">{{ state }}</option>
                                    </select-input>
                                </div>
                                <p v-text="form.errors.state" class="text-red-500 text-sm"></p>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="zip_code" v-bind:class="(form.errors.zip_code)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Zip Code <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="zip_code" v-model="form.zip_code" :error="form.errors.zip_code" type="text" v-bind:class="(form.errors.zip_code)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.zip_code" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-3">
                                <label for="phone" v-bind:class="(form.errors.phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Phone <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="phone"
                                           v-model="form.phone"
                                           :error="form.errors.phone" type="text"
                                           v-bind:class="(form.errors.phone)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.phone" class="text-red-500 text-sm"></p>
                                    <p class="text-gray-500 text-sm">Do not include a leading 1, include the area code, must be 10 digits without dashes or other characters.</p>
                                </div>
                            </div>
                            <div class="sm:col-span-3">
                                <label for="dob" v-bind:class="(form.errors.dob)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Birthday <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <div class="absolute left-0 pl-3 flex items-center pointer-events-none" v-bind:class="(form.errors.dob)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                                    </div>
                                    <input id="dob" v-model="form.dob" :error="form.errors.dob" type="date" v-bind:class="(form.errors.dob)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                                    <p v-text="form.errors.dob" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-3">
                                <label for="ssn" v-bind:class="(form.errors.ssn)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    SSN <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="ssn" v-model="form.ssn" :error="form.errors.ssn" type="password" v-bind:class="(form.errors.ssn)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p class="text-gray-500 text-sm">Do not include dashes.</p>
                                    <p class="text-gray-500 text-sm">We do not store your SSN on our servers. Your SSN is used to verify you with RentRisk.</p>
                                    <p v-text="form.errors.ssn" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="pt-5 grid grid-cols-6 gap-4">
                        <div class="col-end-7 col-span-2 justify-end flex">
                            <inertia-link :href="route('properties.index')" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400">Cancel</inertia-link>
                            <form-button :form="form" type="submit">
                                Save
                            </form-button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </tailwind-layout>
</template>

<script>import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCalendar } from '@fortawesome/pro-light-svg-icons'

import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import SelectInput from "@/Shared/SelectInput.vue"
import { vMaska } from "maska/vue";

export default {
    components: {
        Icon,
        TailwindLayout,
        FormButton,
        LoadingButton,
        SelectInput,
    FontAwesomeIcon
  },
    props: {
        states: Object,
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                user_prefs: null,
                user_prefs2: null,
                first_name: null,
                middle_name: null,
                last_name: null,
                address_1: null,
                address_2: null,
                city: null,
                state: null,
                zip_code: null,
                phone: null,
                dob: null,
                ssn: null,

            }),
            disabled: false,
        }
    },
    methods: {
        isDisabled() {
            this.disabled = !this.disabled;
            return this.form.middle_name = '';
        },
        update() {
            this.form.user_prefs = adx.validate('user_prefs');
            this.form.user_prefs2 = adx.validate('user_prefs2');
            this.form.post(this.route('agent.submit-verification'))
        },
        autofill(person) {
            this.form.first_name = person.first;
            this.form.middle_name = person.middle;
            this.form.last_name = person.last;
            this.form.address_1 = person.address;
            this.form.city = person.city;
            this.form.state = person.state;
            this.form.zip_code = person.zip;
            this.form.phone = person.phone;
            this.form.dob = person.dob;
            this.form.ssn = person.ssn;
        }
    },
    directives: {
        maska: vMaska
    }
}
</script>
